// src/utils/taskFormatters.js
// Pure formatting utility functions extracted from TaskList

export const formatFileSize = (task) => {
  const bytes = task.totalImageSize || task.totalSize || task.size || 0;
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
};

export const formatDuration = (seconds) => {
  if (!seconds) return '0s';

  const mins = Math.floor(seconds / 60);
  const hours = Math.floor(seconds / 3600);
  const days = Math.floor(seconds / 86400);

  // > 1 day: show days
  if (days >= 1) {
    return days === 1 ? '1 day' : `${days} days`;
  }

  // >= 4 hours: show just hours
  if (hours >= 4) {
    return `${hours}h`;
  }

  // < 4 hours but >= 60 mins: show hours and minutes
  if (mins >= 60) {
    const remainingMins = mins % 60;
    return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
  }

  // < 60 mins but >= 10 mins: show just minutes
  if (mins >= 10) {
    return `${mins}m`;
  }

  // < 10 mins: show minutes and seconds
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

export const getLocationCoordinates = (task) => {
  const location = task.location;
  if (!location || typeof location === 'string') return null;
  if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
    return { lat: location.latitude, lng: location.longitude };
  }
  return null;
};

// Task locations render on our own Leaflet map now (LocationMapDialog); this
// survives only as the "Directions" escape hatch inside it, since an in-app
// map can show where a task happened but cannot route a driver there.
export const googleMapsDirectionsUrl = (lat, lng) =>
  `https://www.google.com/maps?q=${lat},${lng}`;

// The operator/reporter label a task card or detail view shows where the
// operator normally goes. Shared by TaskCard, TaskDetailView,
// TaskCaptureRow and SystemAdminLiveTasks so a QR one-off report reads the
// same way everywhere, rather than each screen inventing its own wording
// (coordinator follow-up #7, 2026-09-29 review).
//
// A QR one-off report usually has no operator — a member of the public
// standing at the machine, not a logged-in field operator — so it carries
// `reporter_name` instead of operatorName/operatorId. An operator-token QR
// report DOES carry operatorName (functions' submit_public_issue writes it
// when the request's Bearer token resolves to one, #I2), so that still
// wins. Returns null — not a fallback string — when there is truly nothing
// to show, since callers want different final fallbacks ('Unknown',
// 'Unknown operator', ...).
export const taskOperatorLabel = (task) => {
  if (!task) return null;
  if (task.operatorName) return task.operatorName;
  if (task.operatorId) return task.operatorId.substring(0, 8);
  if (task.source === 'public_qr') {
    return task.reporter_name ? `${task.reporter_name} (public report)` : 'Public report';
  }
  return null;
};

// What the legacy SSS server stored as the address when Google found nothing.
// Every migrated task carrying it had no GPS fix (it was stored at 0,0), so it
// is not a place and is shown as no location.
const LEGACY_NO_ADDRESS = 'unable to geocode';

// The place name a task carries (locationName, else location.address), or
// null when it has none.
export const taskPlaceName = (task) => {
  const candidates = [task?.locationName, task?.location?.address];
  for (const value of candidates) {
    const name = typeof value === 'string' ? value.trim() : '';
    if (name && name.toLowerCase() !== LEGACY_NO_ADDRESS) return name;
  }
  return null;
};

export const formatLocation = (task) => {
  // Priority order: place name > coordinates > fallback
  const name = taskPlaceName(task);
  if (name) return name;

  const location = task.location;
  if (!location) return 'Location not set';

  if (typeof location === 'string') return location;
  if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
  }
  return 'Location not set';
};

export const formatDateTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

export const formatTimeOnly = (timestamp) => {
  if (!timestamp) return '';

  let date;
  if (timestamp.toDate) {
    // Firestore Timestamp
    date = timestamp.toDate();
  } else if (timestamp.seconds) {
    // Firestore Timestamp object format
    date = new Date(timestamp.seconds * 1000);
  } else if (typeof timestamp === 'number') {
    // Unix timestamp
    date = new Date(timestamp);
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return '';
  }

  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const getSortedImages = (images) => {
  if (!images || !Array.isArray(images)) return [];
  return [...images].sort((a, b) => {
    // Sort by sequence number if available, otherwise by timestamp
    if (a.sequence !== undefined && b.sequence !== undefined) {
      return a.sequence - b.sequence;
    }
    // Fallback to timestamp sorting if sequence is not available
    const timestampA = a.timestamp || a.createdAt;
    const timestampB = b.timestamp || b.createdAt;
    if (timestampA && timestampB) {
      const dateA = timestampA.toDate ? timestampA.toDate() : new Date(timestampA);
      const dateB = timestampB.toDate ? timestampB.toDate() : new Date(timestampB);
      return dateA - dateB;
    }
    return 0;
  });
};

export const getActionColor = (action) => {
  switch (action) {
    case 'created':
      return '#2196f3'; // Blue
    case 'image_added':
      return '#4caf50'; // Green
    case 'image_removed':
      return '#e53935'; // Red
    case 'data_updated':
      return '#9c27b0'; // Purple
    case 'submitted':
      return '#ff9800'; // Orange
    case 'uploaded':
      return '#009688'; // Teal
    case 'status_changed':
      return '#607d8b'; // Blue Grey
    case 'upload_progress':
      return '#0288d1'; // Light Blue
    case 'google_drive_upload':
      return '#1a73e8'; // Google Blue
    case 'onedrive_upload':
      return '#0078d4'; // Microsoft Blue
    case 'email_sent':
      return '#00796b'; // Teal
    case 'pdf_downloaded':
      return '#d32f2f'; // PDF Red
    case 'zip_downloaded':
      return '#f57c00'; // Amber
    case 'shared_externally':
      return '#c2185b'; // Magenta — its own colour because it is the one
                        // action whose result TrakLog cannot see or undo
    case 'approved':
      return '#2e7d32'; // Dark Green
    case 'rejected':
      return '#c62828'; // Dark Red
    default:
      return '#757575'; // Grey
  }
};
